/**
 * DataTable - Reusable TanStack Table Component
 * 
 * Features:
 * - Column sorting
 * - Row grouping/expansion
 * - Column pinning (frozen columns)
 * - Excel export
 * - Custom cell rendering
 * - Conditional formatting
 * - Virtualization for large datasets
 */

import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Toolbar,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  ExpandMore,
  ChevronRight,
  FileDownload,
  FilterList,
  ViewColumn,
  Refresh,
  Search,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import EmptyState from './EmptyState';

const DataTable = ({
  data,
  columns,
  title,
  onRefresh,
  enableRowSelection = false,
  enableGrouping = false,
  enableExport = true,
  enableSearch = true,
  enableColumnVisibility = true,
  stickyHeader = true,
  maxHeight = 'calc(100vh - 300px)',
  rowClassName,
  cellClassName,
  onRowClick,
  customToolbarActions,
  exportFileName = 'export',
}) => {
  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
      globalFilter,
      columnVisibility,
      columnSizing,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.subRows,
  });

  // Export to Excel
  const handleExport = () => {
    const exportData = table.getFilteredRowModel().rows.map(row => {
      const rowData = {};
      row.getVisibleCells().forEach(cell => {
        const columnDef = cell.column.columnDef;
        const header = typeof columnDef.header === 'string' 
          ? columnDef.header 
          : columnDef.id;
        
        // Get raw value for export
        const value = cell.getValue();
        rowData[header] = value;
      });
      return rowData;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${exportFileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Get cell styles based on conditions
  const getCellStyle = (cell) => {
    const columnDef = cell.column.columnDef;
    const value = cell.getValue();
    
    // Apply custom cell styles if defined
    if (columnDef.cellStyle) {
      return columnDef.cellStyle(value, cell.row.original);
    }
    
    return {};
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          component="div"
        >
          {title}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Search */}
          {enableSearch && (
            <TextField
              size="small"
              placeholder="Search..."
              value={globalFilter ?? ''}
              onChange={e => setGlobalFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
          )}

          {/* Custom Actions */}
          {customToolbarActions}

          {/* Export */}
          {enableExport && (
            <Tooltip title="Export to Excel">
              <IconButton onClick={handleExport} size="small">
                <FileDownload />
              </IconButton>
            </Tooltip>
          )}

          {/* Refresh */}
          {onRefresh && (
            <Tooltip title="Refresh Data">
              <IconButton onClick={onRefresh} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>

      {/* Table */}
      <TableContainer sx={{ maxHeight }}>
        <Table
          stickyHeader={stickyHeader}
          size="small"
          sx={{ tableLayout: 'fixed', width: table.getTotalSize() }}
        >
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableCell
                    key={header.id}
                    align={header.column.columnDef.align || 'left'}
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      whiteSpace: 'nowrap',
                      width: header.getSize(),
                      minWidth: header.getSize(),
                      maxWidth: header.getSize(),
                      position: 'relative', // Required for resize handle positioning
                      ...(header.column.columnDef.sticky && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 11,
                      }),
                      ...header.column.columnDef.headerStyle,
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                            userSelect: 'none',
                            pr: 2, // padding for resize handle
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column' }}>
                              {header.column.getIsSorted() === 'asc' ? (
                                <ArrowUpward fontSize="small" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ArrowDownward fontSize="small" />
                              ) : (
                                <UnfoldMore fontSize="small" sx={{ opacity: 0.3 }} />
                              )}
                            </Box>
                          )}
                        </Box>
                        {/* Column Resize Handle */}
                        <Box
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '8px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            zIndex: 10,
                            backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                            },
                          }}
                        />
                      </>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ p: 2, border: 'none' }}
                >
                  <EmptyState
                    compact
                    title="No data available"
                  />
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map(row => {
                const isGroupRow = row.subRows && row.subRows.length > 0;
                
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => onRowClick && onRowClick(row.original)}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      bgcolor: isGroupRow ? 'grey.50' : 'transparent',
                      '&:hover': {
                        bgcolor: isGroupRow ? 'grey.100' : 'action.hover',
                      },
                      ...(rowClassName ? rowClassName(row.original) : {}),
                    }}
                  >
                    {row.getVisibleCells().map(cell => {
                      const columnDef = cell.column.columnDef;
                      
                      return (
                        <TableCell
                          key={cell.id}
                          align={columnDef.align || 'left'}
                          sx={{
                            py: 1,
                            px: 2,
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                            whiteSpace: columnDef.wrap ? 'normal' : 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: columnDef.wrap ? 'break-word' : 'normal',
                            position: columnDef.sticky ? 'sticky' : 'relative',
                            left: columnDef.sticky ? 0 : 'auto',
                            bgcolor: columnDef.sticky ? 'background.paper' : 'transparent',
                            zIndex: columnDef.sticky ? 9 : 1,
                            fontWeight: isGroupRow ? 600 : 400,
                            ...getCellStyle(cell),
                            ...(cellClassName ? cellClassName(row.original, cell.column.id) : {}),
                          }}
                        >
                          {/* Expand button for grouped rows */}
                          {cell.column.id === columns[0].id && enableGrouping && row.getCanExpand() && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                row.toggleExpanded();
                              }}
                              sx={{ mr: 1, p: 0 }}
                            >
                              {row.getIsExpanded() ? (
                                <ExpandMore fontSize="small" />
                              ) : (
                                <ChevronRight fontSize="small" />
                              )}
                            </IconButton>
                          )}
                          
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer with row count */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {table.getFilteredRowModel().rows.length} of {data.length} rows
        </Typography>
      </Box>
    </Paper>
  );
};

export default DataTable;
